import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { PublicNavbar } from "@/components/layout/PublicNav";
import { AppFooter } from "@/components/layout/AppFooter";
import { GlowField } from "@/components/ui/GlowField";
import { CtaButton } from "@/components/ui/CtaButton";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { PhoneInput, combinePhone } from "@/components/ui/PhoneInput";
import { COUNTRIES } from "@/lib/countries";
import { tzOptions, browserTz } from "@/lib/tzUtils";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Sparkles, X } from "lucide-react";
import { motion } from "framer-motion";

const PLATFORMS = [
  { value: "google_meet", label: "Google Meet (built-in)", hint: "Recommended" },
  { value: "zoom", label: "Zoom" },
  { value: "teams", label: "Microsoft Teams" },
  { value: "custom", label: "Custom URL" },
];

const LANGUAGES = ["English", "Hindi", "Urdu", "Arabic", "Spanish", "French", "Mandarin", "Bengali", "Tamil", "Telugu", "Marathi", "Russian", "Portuguese", "German", "Japanese", "Korean", "Turkish", "Indonesian"].map((l) => ({ value: l, label: l }));

const SUBJECT_SUGGESTIONS = ["Mathematics", "Physics", "Chemistry", "Biology", "English", "History", "Geography", "Computer Science", "Economics", "Accounting", "Hindi", "Urdu", "Arabic", "French", "Spanish", "Mandarin", "Quran", "Coding", "Robotics", "Art", "Music"];
const GRADE_SUGGESTIONS = ["Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5", "Grade 6", "Grade 7", "Grade 8", "Grade 9", "Grade 10", "Grade 11", "Grade 12", "IGCSE", "A-Levels", "IB", "AP", "JEE", "NEET", "SAT", "Undergrad"];

export default function SignupTeacher() {
  const { signupTeacher } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState({ country: "PK", number: "" });
  const [form, setForm] = useState({
    full_name: "", email: "", password: "",
    country: "PK",
    subjects: [],
    grades: [],
    experience_years: 3,
    teaching_language: "English",
    bio: "",
    availability: "",
    preferred_meeting_platform: "google_meet",
    timezone: browserTz(),
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        phone: combinePhone(phone),
        country: form.country,
        experience_years: Number(form.experience_years) || 0,
      };
      await signupTeacher(payload);
      toast.success("Welcome aboard!");
      nav("/teacher");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FBFAF7] text-[#0A0C10]">
      <PublicNavbar />
      <div className="relative flex-1 overflow-hidden">
        <div aria-hidden className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -left-20 w-[500px] h-[500px] rounded-full opacity-40" style={{ background: "radial-gradient(circle, rgba(255,180,0,0.32), transparent 60%)" }} />
          <div className="absolute -bottom-40 -right-20 w-[500px] h-[500px] rounded-full opacity-30" style={{ background: "radial-gradient(circle, rgba(0,47,167,0.20), transparent 60%)" }} />
        </div>
        <div className="relative max-w-2xl mx-auto px-6 py-12">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#FFB400] inline-flex items-center gap-2 mb-3">
            <span className="w-2 h-2 bg-[#FFB400] inline-block rounded-full" /> Teacher signup · Step {step} of 2 {step === 2 && <span className="text-neutral-500">(optional)</span>}
          </div>
          <h1 className="font-display font-bold text-4xl lg:text-5xl tracking-tighter">{step === 1 ? "Start teaching with AI." : "Tell us a bit more (optional)."}</h1>
          <p className="mt-2 text-neutral-600 text-[15px]">{step === 1 ? "100 free AI credits. No card required." : "All fields below are optional — you can add them later from My Profile."}</p>

          <Progress step={step} />

          <motion.form
            key={step}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.2, 0.7, 0.2, 1] }}
            onSubmit={submit}
            className="bg-white border border-black/8 rounded-2xl shadow-[0_30px_80px_-20px_rgba(10,12,16,0.12)] p-6 sm:p-8 space-y-5"
          >
            {step === 1 && (
              <>
                <GlowField label="Full name" testid="signup-name" required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Ahmad Raza" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <GlowField label="Email" testid="signup-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@school.com" />
                  <GlowField label="Password" testid="signup-password" type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="At least 6 chars" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">Phone / WhatsApp</div>
                    <PhoneInput value={phone} onChange={setPhone} testid="signup-phone" />
                  </div>
                  <div>
                    <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">Country</div>
                    <SearchableSelect
                      testid="signup-country"
                      placeholder="Select country"
                      options={COUNTRIES.map((c) => ({ value: c.code, label: c.name, hint: c.dial, icon: <span>{c.flag}</span> }))}
                      value={form.country}
                      onChange={(v) => set("country", v)}
                    />
                  </div>
                </div>
                <div>
                  <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">Your time zone</div>
                  <SearchableSelect
                    testid="signup-timezone"
                    placeholder="Search your time zone…"
                    options={tzOptions()}
                    value={form.timezone}
                    onChange={(v) => set("timezone", v)}
                  />
                  <p className="mt-1 text-[11px] text-neutral-500">Used to schedule your classes at the right local time. Detected automatically — change it if needed.</p>
                </div>
                <div className="flex justify-end gap-2">
                  <CtaButton
                    type="submit"
                    data-testid="signup-skip-finish"
                    variant="ghost"
                    size="lg"
                    className="rounded-full"
                    disabled={loading || !form.full_name || !form.email || form.password.length < 6}
                  >
                    Skip & finish
                  </CtaButton>
                  <CtaButton
                    type="button"
                    data-testid="signup-next"
                    variant="primary"
                    size="lg"
                    className="rounded-full"
                    disabled={!form.full_name || !form.email || form.password.length < 6}
                    onClick={() => setStep(2)}
                  >
                    Add more details <ArrowRight size={14} />
                  </CtaButton>
                </div>
              </>
            )}
            {step === 2 && (
              <>
                <TagPicker label="Subjects you teach" testidPrefix="signup-subject" suggestions={SUBJECT_SUGGESTIONS} values={form.subjects} onChange={(v) => set("subjects", v)} />
                <TagPicker label="Class levels / grades" testidPrefix="signup-grade" suggestions={GRADE_SUGGESTIONS} values={form.grades} onChange={(v) => set("grades", v)} />
                <div className="grid sm:grid-cols-2 gap-4">
                  <GlowField label="Years of experience" type="number" min={0} value={form.experience_years} onChange={(e) => set("experience_years", e.target.value)} testid="signup-exp" />
                  <div>
                    <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">Teaching language</div>
                    <SearchableSelect
                      testid="signup-lang"
                      placeholder="Select language"
                      options={LANGUAGES}
                      value={form.teaching_language}
                      onChange={(v) => set("teaching_language", v)}
                    />
                  </div>
                </div>
                <div>
                  <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">Preferred live class platform</div>
                  <SearchableSelect
                    testid="signup-platform"
                    placeholder="Select platform"
                    options={PLATFORMS}
                    value={form.preferred_meeting_platform}
                    onChange={(v) => set("preferred_meeting_platform", v)}
                  />
                </div>
                <GlowField label="Short bio" as="textarea" rows={3} value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder="Tell parents & students what makes you a great tutor…" />
                <GlowField label="Teaching availability" placeholder="Mon–Fri 4–9pm IST" value={form.availability} onChange={(e) => set("availability", e.target.value)} />
                <div className="flex flex-wrap gap-3 justify-between">
                  <CtaButton type="button" variant="ghost" size="lg" className="rounded-full" onClick={() => setStep(1)}>
                    <ArrowLeft size={14} /> Back
                  </CtaButton>
                  <CtaButton data-testid="signup-submit" type="submit" variant="primary" size="lg" className="rounded-full" disabled={loading}>
                    <Sparkles size={14} /> {loading ? "Creating…" : "Create account"}
                  </CtaButton>
                </div>
              </>
            )}
          </motion.form>

          <div className="mt-6 text-sm text-neutral-600">
            Already a teacher? <Link to="/login" className="text-[#0A0C10] font-semibold underline underline-offset-4 decoration-[#FFB400]/50 hover:text-[#FFB400] transition-colors">Sign in</Link>
          </div>
        </div>
      </div>
      <AppFooter variant="light" />
    </div>
  );
}

function Progress({ step }) {
  return (
    <div className="my-6 flex items-center gap-3">
      <div className={`h-1 flex-1 rounded-full ${step >= 1 ? "bg-[#0A0C10]" : "bg-black/10"}`} />
      <div className={`h-1 flex-1 rounded-full ${step >= 2 ? "bg-[#0A0C10]" : "bg-black/10"}`} />
    </div>
  );
}

function TagPicker({ label, suggestions, values, onChange, testidPrefix }) {
  const remaining = useMemo(() => suggestions.filter((s) => !values.includes(s)).map((s) => ({ value: s, label: s })), [suggestions, values]);
  return (
    <div>
      <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">{label}</div>
      <div className="flex flex-wrap gap-2 mb-2" data-testid={`${testidPrefix}-tags`}>
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1.5 bg-[#0A0C10] text-[#FBFAF7] text-xs px-2.5 py-1 rounded-full">
            {v}
            <button type="button" onClick={() => onChange(values.filter((x) => x !== v))} className="hover:text-[#FFB400]" data-testid={`${testidPrefix}-remove-${v}`}><X size={10} /></button>
          </span>
        ))}
        {values.length === 0 && <span className="text-xs text-neutral-500">None selected yet</span>}
      </div>
      <SearchableSelect
        testid={`${testidPrefix}-add`}
        placeholder="+ Add another"
        searchPlaceholder="Search or type…"
        options={remaining}
        value=""
        onChange={(v) => onChange([...values, v])}
      />
    </div>
  );
}
